import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import Confetti from "react-confetti";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Reel } from "./Reel";
import {
  BASE_SPIN_MS,
  REEL_COUNT,
  REEL_STOP_STAGGER_MS,
  SlotMachineProps,
  SlotSymbol,
  SpinOutcome,
} from "./types";
import { evaluateSpin, generateSpinResult } from "./utils";

const INITIAL_DISPLAY: SlotSymbol[] = ["🍒", "💎", "⭐"];

/** Placeholder — wire to canvas/confetti lib when integrating rewards */
export function triggerConfettiPlaceholder(): void {
  // Intentionally empty; parent toggles Confetti via state
}

export function SlotMachine({ className, onSpinComplete }: SlotMachineProps) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [spinKey, setSpinKey] = useState(0);
  const [result, setResult] = useState<SlotSymbol[]>(INITIAL_DISPLAY);
  const [winningSymbol, setWinningSymbol] = useState<SlotSymbol | null>(null);
  const [isJackpot, setIsJackpot] = useState(false);
  const [showWinModal, setShowWinModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [leverPulled, setLeverPulled] = useState(false);
  const [stoppedCount, setStoppedCount] = useState(0);
  const [confettiSize, setConfettiSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const update = () =>
      setConfettiSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const handleReelStopped = useCallback(() => {
    setStoppedCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!isSpinning || stoppedCount < REEL_COUNT) return;

    const outcome = evaluateSpin(result);
    setIsSpinning(false);
    setLeverPulled(false);
    onSpinComplete?.(outcome);

    if (outcome.isWin) {
      setWinningSymbol(outcome.winningSymbol);
      setIsJackpot(outcome.isJackpot);
      triggerConfettiPlaceholder();
      setShowConfetti(true);
      setShowWinModal(true);
      const timer = window.setTimeout(() => setShowConfetti(false), 5000);
      return () => window.clearTimeout(timer);
    }

    setWinningSymbol(null);
    setIsJackpot(false);
    return undefined;
  }, [isSpinning, stoppedCount, result, onSpinComplete]);

  const handleSpin = useCallback(() => {
    if (isSpinning) return;

    const nextResult = generateSpinResult();
    setResult(nextResult);
    setSpinKey((k) => k + 1);
    setStoppedCount(0);
    setWinningSymbol(null);
    setIsJackpot(false);
    setShowWinModal(false);
    setShowConfetti(false);
    setIsSpinning(true);
    setLeverPulled(true);
  }, [isSpinning]);

  const isWinningReel = isSpinning === false && winningSymbol !== null;

  return (
    <>
      {showConfetti && confettiSize.width > 0 && (
        <Confetti
          width={confettiSize.width}
          height={confettiSize.height}
          numberOfPieces={350}
          recycle={false}
          colors={["#fbbf24", "#a855f7", "#f472b6", "#fde68a", "#c084fc"]}
        />
      )}

      <Card
        className={cn(
          "relative overflow-hidden border-2 border-amber-500/30",
          "bg-gradient-to-br from-[#12081f] via-[#1a0f2e] to-[#0c0614]",
          "shadow-[0_0_60px_rgba(168,85,247,0.25),0_20px_50px_rgba(0,0,0,0.6)]",
          className
        )}
      >
        <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-purple-600/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-amber-500/15 blur-3xl" />

        <CardContent className="relative p-4 sm:p-8">
          <div className="mb-6 text-center">
            <div className="mb-1 flex items-center justify-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-400" />
              <h2 className="bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300 bg-clip-text text-xl font-bold tracking-wider text-transparent sm:text-2xl">
                LUCKY SLOTS
              </h2>
              <Sparkles className="h-5 w-5 text-amber-400" />
            </div>
            <p className="text-xs text-purple-200/70 sm:text-sm">
              Match three symbols to win · Triple 7️⃣ = JACKPOT
            </p>
          </div>

          <motion.div
            animate={
              isSpinning
                ? { x: [0, -3, 3, -2, 2, 0] }
                : { x: 0 }
            }
            transition={
              isSpinning
                ? { repeat: Infinity, duration: 0.35, ease: "easeInOut" }
                : { duration: 0.2 }
            }
            className="mx-auto flex max-w-lg flex-col items-center gap-6 sm:flex-row sm:justify-center"
          >
            {/* Machine body + reels */}
            <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-b from-zinc-900/90 to-black/90 p-4 shadow-[inset_0_2px_20px_rgba(168,85,247,0.15)] sm:p-5">
              <div className="mb-3 flex justify-center gap-1">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-1.5 w-1.5 rounded-full bg-amber-400/80 shadow-[0_0_6px_rgba(251,191,36,0.8)]"
                  />
                ))}
              </div>

              <div className="flex gap-2 sm:gap-3">
                {result.map((symbol, index) => (
                  <Reel
                    key={`${spinKey}-${index}`}
                    spinKey={spinKey}
                    targetSymbol={symbol}
                    isSpinning={isSpinning}
                    stopDelay={
                      BASE_SPIN_MS + index * REEL_STOP_STAGGER_MS
                    }
                    isWinning={
                      isWinningReel && symbol === winningSymbol
                    }
                    onStopped={handleReelStopped}
                  />
                ))}
              </div>

              <div className="mt-3 flex justify-center gap-1">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-1 w-1 rounded-full bg-purple-400/60"
                  />
                ))}
              </div>
            </div>

            {/* Lever */}
            <div className="flex flex-col items-center gap-2">
              <motion.button
                type="button"
                aria-label="Pull lever to spin"
                disabled={isSpinning}
                onClick={handleSpin}
                className={cn(
                  "group relative flex flex-col items-center",
                  "disabled:cursor-not-allowed disabled:opacity-60"
                )}
                whileTap={!isSpinning ? { scale: 0.95 } : undefined}
              >
                <motion.div
                  className="origin-bottom"
                  animate={{
                    rotate: leverPulled ? 32 : 0,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 280,
                    damping: 18,
                  }}
                >
                  <div className="h-16 w-3 rounded-full bg-gradient-to-b from-amber-300 to-amber-600 shadow-[0_0_12px_rgba(251,191,36,0.5)]" />
                  <div className="mx-auto -mt-1 h-7 w-7 rounded-full border-2 border-amber-400 bg-gradient-to-br from-red-500 to-red-700 shadow-lg" />
                </motion.div>
                <span className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-amber-400/80">
                  Pull
                </span>
              </motion.button>
            </div>
          </motion.div>

          {/* SPIN button */}
          <div className="mt-8 flex justify-center">
            <Button
              size="lg"
              disabled={isSpinning}
              onClick={handleSpin}
              className={cn(
                "h-14 min-w-[200px] rounded-full text-lg font-bold tracking-widest",
                "bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500",
                "text-purple-950 shadow-[0_0_30px_rgba(251,191,36,0.45)]",
                "hover:from-amber-400 hover:via-yellow-300 hover:to-amber-400",
                "disabled:opacity-70"
              )}
            >
              {isSpinning ? "SPINNING..." : "SPIN"}
            </Button>
          </div>

          {/* Pay table */}
          <div className="mt-8 grid grid-cols-3 gap-2 sm:grid-cols-6">
            {(["🍒", "💎", "⭐", "🎁", "7️⃣", "🍋"] as SlotSymbol[]).map(
              (sym) => (
                <div
                  key={sym}
                  className="flex flex-col items-center rounded-lg border border-purple-500/20 bg-black/30 px-2 py-2"
                >
                  <span className="text-2xl">{sym}</span>
                  <span className="mt-0.5 text-[10px] text-purple-200/60">
                    {sym === "7️⃣" ? "JACKPOT" : "3× match"}
                  </span>
                </div>
              )
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showWinModal} onOpenChange={setShowWinModal}>
        <DialogContent className="border-amber-500/30 bg-gradient-to-b from-[#1a0f2e] to-[#0c0614] text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">
              {isJackpot ? (
                <span className="bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 bg-clip-text text-transparent">
                  🎰 JACKPOT! 🎰
                </span>
              ) : (
                <span className="text-amber-300">You Win!</span>
              )}
            </DialogTitle>
            <DialogDescription className="text-center text-purple-200/80">
              {isJackpot
                ? "Three lucky sevens! You've hit the grand prize."
                : `Three matching ${winningSymbol} symbols — amazing luck!`}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center gap-3 py-4">
            {result.map((sym, i) => (
              <div
                key={i}
                className="flex h-16 w-14 items-center justify-center rounded-xl border-2 border-amber-400/60 bg-black/50 text-3xl shadow-[0_0_20px_rgba(251,191,36,0.4)]"
              >
                {sym}
              </div>
            ))}
          </div>

          <DialogFooter className="sm:justify-center">
            <Button
              onClick={() => setShowWinModal(false)}
              className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600"
            >
              Play Again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
